import React, { useState, useRef, useEffect } from 'react';
import './customSelect.css';

/**
 * Accessible custom dropdown used for the model / engine / year filters.
 * Replaces the native <select> so the option list can carry the same
 * red-tint hover as the rest of the UI. Opens upward because the filters
 * sit at the bottom of the screen.
 */
const CustomSelect = ({ value, onChange, options, placeholder, disabled = false, emphasis = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape for keyboard users
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const selected = options.find((o) => o.value === value && o.value !== '');

  const handleSelect = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div
      className={`cselect${disabled ? ' cselect-disabled' : ''}${emphasis ? ' cselect-emphasis' : ''}`}
      ref={ref}
    >
      <button
        type="button"
        className={`cselect-header${open ? ' open' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`cselect-value${selected ? '' : ' placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="cselect-arrow" aria-hidden="true">▾</span>
      </button>

      {open && !disabled && (
        <div className="cselect-menu" role="listbox">
          {options.map((o) => (
            <div
              key={`${o.value}-${o.label}`}
              role="option"
              aria-selected={o.value === value}
              className={`cselect-option${o.value === value ? ' selected' : ''}${o.value === '' ? ' cselect-option-clear' : ''}`}
              onClick={() => handleSelect(o.value)}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
